import React, { useState, useEffect } from 'react';
import './CompatibilityDisplay.css';

interface CompatibilityNote {
  message: string;
  status: boolean;
}

interface CompatibilityData {
  legacyPowerCompatible: CompatibilityNote[];
  usbcPowerCompatible: CompatibilityNote[];
  usbcCommunicationAndVideoCompatible: CompatibilityNote[];
  usbaCommunicationAndVideoCompatible: CompatibilityNote[];
  mappingCalculation?: boolean;
}

interface Accessory {
  dockCompatible?: string[];
  isCompatible?: boolean;
  details: any;
}

interface CompatibilityDisplayProps {
  compatibilityData: CompatibilityData;
  accessory: Accessory;
  compatibilityLogic: Array<{ label: string; value: boolean; key: string }>;
}

const CompatibilityDisplay: React.FC<CompatibilityDisplayProps> = ({ 
  compatibilityData, 
  accessory, 
  compatibilityLogic 
}) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  // Initialize all sections as expanded
  useEffect(() => {
    const initialExpandedState: Record<string, boolean> = {};
    Object.keys(compatibilityData).forEach(section => {
      initialExpandedState[section] = true;
    });
    setExpandedSections(initialExpandedState);
  }, [compatibilityData]);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getStatusIcon = (isCompatible: boolean) => {
    return isCompatible ? 
      <i className="fas fa-circle-check text-success fs-2x mb-3"></i> :
      <i className="fas fa-circle-xmark text-danger fs-2x mb-3"></i>;
  };

  const getCompatibilityStatus = () => {
    if (accessory.isCompatible) {
      return {
        status: 'compatible',
        message: 'Compatible',
        type: getCompatibilityType(accessory.dockCompatible)
      };
    }
    return {
      status: 'incompatible',
      message: 'Not Compatible',
      type: null
    };
  };

  const getCompatibilityType = (dockCompatible: string[] | undefined) => {
    if (!dockCompatible) return 'other';
    if (dockCompatible.some(type => type.includes('Legacy'))) return 'legacy';
    if (dockCompatible.some(type => type.includes('USB-C'))) return 'usbc';
    if (dockCompatible.some(type => type.includes('USB-A'))) return 'usba';
    return 'other';
  };

  const getSectionDisplayName = (sectionKey: string) => {
    const nameMap: Record<string, string> = {
      legacyPowerCompatible: 'Legacy Power Compatibility',
      usbcPowerCompatible: 'USB-C Power Compatibility',
      usbcCommunicationAndVideoCompatible: 'USB-C Communication & Video Compatibility',
      usbaCommunicationAndVideoCompatible: 'USB-A Communication & Video Compatibility'
    };
    
    return nameMap[sectionKey] || sectionKey.replace(/([A-Z])/g, ' $1').replace(/^./, (str: string) => str.toUpperCase());
  };

  const getSectionStatus = (sectionNotes: CompatibilityNote[]) => {
    if (!sectionNotes || sectionNotes.length === 0) return 'empty';
    const lastNote = sectionNotes[sectionNotes.length - 1];
    return lastNote.status ? 'compatible' : 'incompatible';
  };

  const getSectionStatusIcon = (sectionNotes: CompatibilityNote[]) => {
    const status = getSectionStatus(sectionNotes);
    switch (status) {
      case 'compatible':
        return <i className="fas fa-circle-check text-success mr-2 fs-2"></i>;
      case 'incompatible':
        return <i className="fas fa-circle-xmark text-danger mr-2 fs-2"></i>;
      default:
        return <i className="fas fa-circle-question text-warning mr-2 fs-2"></i>;
    }
  };

  const compatibilityStatus = getCompatibilityStatus();

  // Check if all sections are empty
  const allSectionsEmpty = Object.values(compatibilityData).every(
    section => !section || section.length === 0
  );

  console.log("compatibilityData", compatibilityData);

  return (
    <div className="compatibility-container">
      {/* Overall Status Banner */}
      <div className={`status-banner ${compatibilityStatus.status}`}>
        <div className="status-content">
          <span className="status-icon">
            {compatibilityStatus.status === 'compatible' ? 
              <i className="fas fa-circle-check text-success fs-2x"></i> : 
              <i className="fas fa-circle-xmark text-danger fs-2x"></i>
            }
          </span>
          <div>
            <h2 className="status-title">
              {compatibilityStatus.message}
            </h2>
            <p className="status-subtitle">
              {compatibilityStatus.status === 'compatible' 
                ? 'This accessory is compatible with your product'
                : 'This accessory may not be compatible with your product'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Additional Information */}
      <div className="additional-info mb-5">
        <h4>Compatibility Summary</h4>
        <div className="info-grid">
          <div className="info-item">
            <label>Final Compatibility:</label>
            <span className={`status-badge ${compatibilityStatus.status}`}>
              {compatibilityStatus.message}
            </span>
          </div>
          
          <div className="info-item status-badge-success">
            <label>Compatible Logic:</label>
            {compatibilityLogic.map((item, index) => (
              <span key={index} className={`status-badge mr-2 ${item.value ? 'compatible' : 'incompatible'}`}>
                {item.label}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Compatibility Details */}
      <div className="compatibility-details">
        {allSectionsEmpty ? (
          <div className="no-compatibility-data">
            <p>No compatibility data available</p>
          </div>
        ) : (
          Object.entries(compatibilityData).map(([sectionKey, sectionNotes]) => {
            // Only render sections that have notes
            if (!sectionNotes || sectionNotes.length === 0 ) {
              return null;
            }

            const sectionStatus = getSectionStatus(sectionNotes);

            return (
              <div key={sectionKey} className={`compatibility-section ${sectionStatus}`}>
                <div 
                  className="section-header"
                  onClick={() => toggleSection(sectionKey)}
                >
                  <div className="section-title">
                    {getSectionStatusIcon(sectionNotes)}
                    <h3>{getSectionDisplayName(sectionKey)}</h3>
                    <span className="notes-count">
                      ({sectionNotes.length} checks)
                    </span>
                    <span className={`section-status-badge ${sectionStatus}`}>
                      {sectionStatus === 'compatible' ? 'Compatible' : 
                       sectionStatus === 'incompatible' ? 'Not Compatible' : 'No Data'}
                    </span>
                  </div>
                  <span className="toggle-icon">
                    {expandedSections[sectionKey] ? '▼' : '▶'}
                  </span>
                </div>
                
                {expandedSections[sectionKey] && (
                  <div className="section-content">
                    <ul className="compatibility-list">
                      {sectionNotes.map((note: CompatibilityNote, index: number) => (
                        <li key={index} className={`compatibility-item ${note.status ? 'compatible' : 'incompatible'}`}>
                          <span className="check-icon">
                            {note.status ? 
                              <i className="fas fa-check-circle text-success fs-5"></i> : 
                              <i className="fas fa-times-circle text-danger fs-5"></i>
                            }
                          </span>
                          <span className="note-text">{note.message}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default CompatibilityDisplay;